import clsx from 'clsx';
import SearchIcon from '~/icons/search.svg';
import { useTranslation } from '~/shared/utils/translation';
import styles from './ProductZipCheck.module.scss';
import { ZIP_REGEX } from './constants';

const ZIP_LENGTH = 4;
const ZIP_INPUT_ID = 'product-zip-check';

const normalizeZip = (value: string) => value.replace(/[^0-9]/g, '').slice(0, ZIP_LENGTH);

interface Props extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
    value: string;
    isInvalid?: boolean;
    onChange(zip: string): void;
}

/**
 * Reports whether a postcode can be delivered to. It holds nothing and acts on nothing - the
 * caller owns the value and decides what a deliverable answer is worth.
 */
export const ProductZipCheck: React.FC<Props> = ({
    className,
    value,
    isInvalid,
    onChange,
    ...rest
}) => {
    const { translate } = useTranslation();

    return (
        <div
            className={clsx(
                styles.productZipCheck,
                {
                    [styles.isInvalid]: isInvalid,
                },
                className,
            )}
            {...rest}
        >
            <SearchIcon className={styles.productZipCheckIcon} aria-hidden />

            <input
                className={styles.productZipCheckInput}
                id={ZIP_INPUT_ID}
                type="text"
                inputMode="numeric"
                maxLength={ZIP_LENGTH}
                // Shares its source with the check itself, so the two can never disagree.
                pattern={ZIP_REGEX.source}
                value={value}
                placeholder={translate('pdp.sameDayDelivery.zipCodePlaceholderText')}
                aria-label={translate('pdp.sameDayDelivery.zipCodeTitle')}
                aria-invalid={isInvalid}
                onChange={(event) => onChange(normalizeZip(event.target.value))}
            />
        </div>
    );
};
